import { Button } from '@packages/shared/src/ui/Button/Button';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { SimilarProductsActionFragmentFragmentDoc } from '@packages/gql/generated/shopping/SimilarProductsActionFragmentFragmentDoc';
import { PrudsysProductsSimilarRecoSlider } from '@packages/recommendations/src/components/ProductsRecoSliders/PrudsysProductsSimilarRecoSlider/PrudsysProductsSimilarRecoSlider.client';
import { SearchReplaceProductsRecoSlider } from '@packages/recommendations/src/components/ProductsRecoSliders/SearchReplaceProductsRecoSlider/SearchReplaceProductsRecoSlider.client';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { dispatchButtonTracking } from '@packages/shared/src/ui/Button/dispatchButtonTracking';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';

export interface SimilarProductActionProps {
  data: FragmentType<typeof SimilarProductsActionFragmentFragmentDoc>;
  className?: string;
}
export const messages = defineMessages({
  similarItemsButtonText: {
    id: 'SimilarProductsAction.Button.Text',
    defaultMessage: 'ähnliche Artikel',
  },
  similarItemsDialogTitle: {
    id: 'SimilarProductsAction.Dialog.Headline',
    defaultMessage: 'Ähnliche Artikel',
  },
});

/* GraphQL */ `
  fragment SimilarProductsActionFragment on WishlistLineItem {
    extra {
      brand
      category
      orderNumber
    }
  }
`;

export const SimilarProductAction: React.FC<SimilarProductActionProps> = ({ data, className }) => {
  const {
    order: {
      recommendations: {
        wishlist: { similarProducts: recoConfig },
      },
    },
  } = useConfig();
  const [isSimilarProductsDialogOpen, setIsSimilarProductsDialogOpen] = useState(false);
  const { formatMessage } = useIntl();
  const dispatchGlycerin = useTracking();

  const handleOpenSimilarProducts = () => {
    dispatchButtonTracking({
      eventDispatcher: dispatchGlycerin,
      type: 'button',
      clickTrackingProps: {
        category: 'Ähnliche Artikel',
        label: formatMessage(messages.similarItemsButtonText),
      },
    });
    setIsSimilarProductsDialogOpen(true);
  };
  const { extra } = getFragmentData(SimilarProductsActionFragmentFragmentDoc, data);

  if (extra === null) {
    return null;
  }

  if (recoConfig.isEnabled) {
    return (
      <>
        <Button onClick={handleOpenSimilarProducts} className={`w-full ${className}`}>
          {formatMessage(messages.similarItemsButtonText)}
        </Button>
        {isSimilarProductsDialogOpen && (
          <Dialog
            isOpen={isSimilarProductsDialogOpen}
            onClose={() => setIsSimilarProductsDialogOpen(false)}
          >
            <DialogHeader onClose={() => setIsSimilarProductsDialogOpen(false)}>
              {formatMessage(messages.similarItemsDialogTitle)}
            </DialogHeader>
            <DialogContent className="bg-color-grey-light px-3 pt-3 pb-[5.25rem]">
              {/* This div is for the dialog size */}
              {/* Without that placeholder the dialog opens very slim and does not resize when the rde is loaded */}
              <div className="md:min-w-xl" />
              {recoConfig.type === 'searchReplace' ? (
                <SearchReplaceProductsRecoSlider
                  preferTitle
                  title=""
                  pageTemplate="wishlist"
                  productId={extra?.orderNumber ?? ''}
                  size=""
                  elementIndex={0}
                />
              ) : (
                <PrudsysProductsSimilarRecoSlider
                  preferTitle
                  title=""
                  categoryIds={extra?.category ? [extra?.category] : []}
                  pageTemplate="wishlist"
                  productId={extra?.orderNumber ?? ''}
                  brand={extra?.brand ?? ''}
                  elementIndex={0}
                />
              )}
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }

  return null;
};
